import React, { useState, useRef, useEffect } from 'react';
import { Input, Popup } from 'semantic-ui-react';
import styled from 'styled-components';
import CustomIcon from '../CustomIcon';
import { useTranslation } from 'react-i18next';

const EditableLabel = styled.div<{ isEditing: boolean }>`
  display: inline-block;
  padding: 6px 10px;
  outline: none;
  border: ${({ isEditing }) => (isEditing ? '1px solid #7AB800' : '1px solid transparent')};
  border-radius: 4px;
  cursor: text;
  transition: background 0.3s, border-color 0.3s;

  &:hover {
    border-color: ${props => props.theme?.secondColor} !important;
    outline: none !important;
  }
`;

const StyledInput = styled(Input)`
  margin-left: 10px !important;
  input {
    border-radius: 4px !important;
  }
`;

const StyledWrapper = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: baseline;
  flex-direction: column;
  gap: 5px; 

  .disabled {
    opacity: 0.9 !important;
  }
`;

interface EditableInputProps {
  label: string;
  value: string;
  onLabelChange?: (newLabel: string) => void;
  onValueChange?: (newValue: string) => void;
  disabled: boolean;
  handleDelete?: () => void
  unit?: string
  max?: number
}

const EditableLabelInput: React.FC<EditableInputProps> = (props) => {
  const { t } = useTranslation()
  const { label, value, onLabelChange, onValueChange, disabled } = props;
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [internalLabel, setInternalLabel] = useState(label);
  const labelRef = useRef<HTMLDivElement>(null);
  const cursorPosition = useRef<number>(0);
  const [unit, setUnit] = useState(props.unit || '')

  useEffect(() => {
    setInternalLabel(label.replace(props.unit, '').trim());
  }, [label, props.unit]);

  const handleLabelClick = () => {
    setIsEditingLabel(true);
    setTimeout(() => {
      labelRef.current?.focus();
    }, 0);
  };

  const handleLabelBlur = () => {
    setIsEditingLabel(false);
    //eslint-disable-next-line
    const label = internalLabel.match(/([^\(]+)\s*\(([^)]+)\)/).slice(1)
    onLabelChange(label[0]);
  };

  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection && selection.anchorNode) {
      cursorPosition.current = selection.anchorOffset;
    }
  };

  const restoreCursorPosition = () => {
    const selection = window.getSelection();
    if (selection && labelRef.current?.firstChild) {
      const range = document.createRange();
      range.setStart(labelRef.current.firstChild, cursorPosition.current);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const handleLabelInput = (e: React.FormEvent<HTMLDivElement>) => {
    saveCursorPosition();
    setInternalLabel(e.currentTarget.textContent || '');
  };

  useEffect(() => {
    if (isEditingLabel) {
      restoreCursorPosition();
    }
  }, [internalLabel, isEditingLabel]);

  return (
    <StyledWrapper>
      <EditableLabel
        ref={labelRef}
        contentEditable={!disabled && isEditingLabel}
        suppressContentEditableWarning={true}
        spellCheck={false}
        isEditing={!disabled && isEditingLabel}
        onClick={handleLabelClick}
        onBlur={handleLabelBlur}
        onInput={handleLabelInput}
      >
        {internalLabel}
      </EditableLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <StyledInput
          type='text'
          disabled={props.disabled}
          value={unit ? `${value} ${unit}` : value}
          onFocus={() => setUnit('')}
          onChange={(e) => {
            if (isNaN(e.target.value) || !e.target.value) {
              onValueChange('')
              return
            }
            if (parseInt(e.target.value.replace(props.unit, '').trim()) < props.max) {
              onValueChange(e.target.value.replace(props.unit, '').trim())
            }
          }
          }
          onBlur={() => {
            if (props.unit) {
              setUnit(props.unit)
            }
          }}
        />
        {!disabled && <Popup
          content={t('Remove performance group')}
          trigger={<CustomIcon onClick={() => props.handleDelete()} size='large' name='trash' />}
        />}
      </div>
    </StyledWrapper>
  );
};

export default EditableLabelInput;
